import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';

const statusOptions = [
  { value: '', label: 'Бүгд' },
  { value: 'pending', label: 'Хүлээгдэж байна' },
  { value: 'confirmed', label: 'Баталгаажсан' },
  { value: 'in-progress', label: 'Хийгдэж байна' },
  { value: 'completed', label: 'Дууссан' },
  { value: 'cancelled', label: 'Цуцлагдсан' }
];

const statusLabel = Object.fromEntries(statusOptions.filter((item) => item.value).map((item) => [item.value, item.label]));

const formatDate = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('mn-MN');
};

const AdminServiceBookingManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [updatingId, setUpdatingId] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailStatus, setDetailStatus] = useState('pending');
  const [detailInternalNote, setDetailInternalNote] = useState('');

  const loadItems = async (nextStatus = statusFilter) => {
    setLoading(true);
    setError('');

    try {
      const params = {};
      if (nextStatus) {
        params.status = nextStatus;
      }

      const response = await api.get('/service-bookings', { params });
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Захиалгын жагсаалт уншиж чадсангүй');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems('');
  }, []);

  const filteredItems = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return items;
    }

    return items.filter((item) => [
      item.username,
      item.phone,
      item.carModel,
      item.plateNumber,
      item.serviceType,
      item.note
    ]
      .join(' ')
      .toLowerCase()
      .includes(query));
  }, [items, searchText]);

  const applyStatusFilter = async (value) => {
    setStatusFilter(value);
    await loadItems(value);
  };

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    setMessage('');
    setError('');

    try {
      const response = await api.patch(`/service-bookings/${id}/status`, { status });
      const updated = response.data?.booking;

      setItems((prev) => prev.map((item) => (item.id === id ? { ...item, status: updated?.status || status } : item)));
      setMessage('Захиалгын төлөв шинэчлэгдлээ');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Төлөв шинэчлэх үед алдаа гарлаа');
    } finally {
      setUpdatingId('');
    }
  };

  const openDetails = (item) => {
    setSelectedBooking(item);
    setDetailStatus(item.status);
    setDetailInternalNote(item.internalNote || '');
  };

  const saveDetails = async () => {
    if (!selectedBooking?.id) {
      return;
    }

    setUpdatingId(selectedBooking.id);
    setMessage('');
    setError('');

    try {
      const response = await api.patch(`/service-bookings/${selectedBooking.id}/status`, {
        status: detailStatus,
        internalNote: detailInternalNote
      });

      const updated = response.data?.booking;

      setItems((prev) => prev.map((item) => (item.id === selectedBooking.id ? { ...item, ...updated } : item)));
      setSelectedBooking((prev) => (prev ? { ...prev, ...updated } : prev));
      setMessage('Захиалгын дэлгэрэнгүй шинэчлэгдлээ');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Захиалгын дэлгэрэнгүй хадгалах үед алдаа гарлаа');
    } finally {
      setUpdatingId('');
    }
  };

  return (
    <div className="card admin-dark-surface" style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Засварын захиалга удирдлага</h3>
        <button onClick={() => loadItems(statusFilter)} disabled={loading}>Шинэчлэх</button>
      </div>

      <div className="form" style={{ marginTop: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <select value={statusFilter} onChange={(event) => applyStatusFilter(event.target.value)}>
          {statusOptions.map((item) => (
            <option key={item.value || 'all'} value={item.value}>{item.label}</option>
          ))}
        </select>
        <input
          placeholder="Нэр, утас, машин, дугаараар хайх"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
      </div>

      {message && <p className="success">{message}</p>}
      {error && <p className="error">{error}</p>}
      {loading && <p>Уншиж байна...</p>}

      {!loading && (
        <div className="form" style={{ marginTop: 10 }}>
          {filteredItems.map((item) => (
            <article key={item.id} className="card admin-light-surface" style={{ marginBottom: 8 }}>
              <strong>{item.carModel} ({item.plateNumber})</strong>
              <div>Хэрэглэгч: {item.username} / {item.phone}</div>
              <div>Үйлчилгээ: {item.serviceType}</div>
              <div>Огноо: {formatDate(item.preferredDate)}</div>
              <div>Тайлбар: {item.note || '-'}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                <span>Одоогийн төлөв: <strong>{statusLabel[item.status] || item.status}</strong></span>
                <select
                  value={item.status}
                  onChange={(event) => updateStatus(item.id, event.target.value)}
                  disabled={updatingId === item.id}
                >
                  {statusOptions.filter((option) => option.value).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <button type="button" onClick={() => openDetails(item)}>Дэлгэрэнгүй</button>
              </div>
            </article>
          ))}

          {filteredItems.length === 0 && <p>Захиалга олдсонгүй</p>}
        </div>
      )}

      {selectedBooking && (
        <div className="modal-backdrop" onClick={() => setSelectedBooking(null)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3>Захиалгын дэлгэрэнгүй</h3>
            <div className="form">
              <div><strong>Хэрэглэгч:</strong> {selectedBooking.username}</div>
              <div><strong>Утас:</strong> {selectedBooking.phone}</div>
              <div><strong>Машин:</strong> {selectedBooking.carModel}</div>
              <div><strong>Улсын дугаар:</strong> {selectedBooking.plateNumber}</div>
              <div><strong>Үйлчилгээ:</strong> {selectedBooking.serviceType}</div>
              <div><strong>Огноо:</strong> {formatDate(selectedBooking.preferredDate)}</div>
              <div><strong>Хэрэглэгчийн тайлбар:</strong> {selectedBooking.note || '-'}</div>

              <select value={detailStatus} onChange={(event) => setDetailStatus(event.target.value)}>
                {statusOptions.filter((option) => option.value).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <textarea
                rows={5}
                placeholder="Дотоод тэмдэглэл (admin)"
                value={detailInternalNote}
                onChange={(event) => setDetailInternalNote(event.target.value)}
              />

              <button type="button" onClick={saveDetails} disabled={updatingId === selectedBooking.id}>
                {updatingId === selectedBooking.id ? 'Хадгалж байна...' : 'Хадгалах'}
              </button>
              <button type="button" onClick={() => setSelectedBooking(null)}>Хаах</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServiceBookingManager;
